import React, { useEffect, useState } from "react";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  MinusOutlined,
} from "@ant-design/icons";
import {
  Form,
  Input,
  Button,
  message,
  Popconfirm,
  Switch,
  InputNumber,
  Row,
  Col,
  Select,
} from "antd";
import TextArea from "antd/es/input/TextArea";
import ButtonWrapper from "../../../components/reuseComp/ButtonWrapper";
import UploadFoodImage from "../../../../lib/UploadFoodImage";
import { getSingleImageUrl, removeSpecialChars } from "../../../../lib/utils";
import {
  useBlogDetails,
  useBlogStatus,
  useDeleteBlog,
  useListBlog,
  useSaveBlog,
} from "../../../../hooks/api/blog/hook";
import { IBlogListPayload, IBlogPayload } from "../../../../types/api/blog";
import { IAuthorPayload } from "../../../../types/api/author";
import MyPagination from "../../../components/Pagination";
import { LoaderCircleIcon } from "lucide-react";

interface Props {
  refetch?: () => void;
  authors: IAuthorPayload[];
  refetchAuthors: () => void;
  allowURLStateUpdate?: boolean;
}

const BlogListingSection: React.FC<Props> = ({
  refetch: externalRefetch,
  authors,
  refetchAuthors,
  allowURLStateUpdate = true,
}) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);
  const [selectedBlogId, setSelectedBlogId] = useState<string | null>(null);

  const DEFAULT_NUQS_CONFIG = {
    throttleMs: !allowURLStateUpdate ? Infinity : 0,
  };

  const { data: blogData, refetch: refetchBlogs } = useListBlog(
    { page: 1, limit: 20, ...DEFAULT_NUQS_CONFIG },
    { refetchOnWindowFocus: false, staleTime: 0 },
  );

  const blogList: IBlogListPayload[] = blogData?.data || [];
  const qData = blogData?.qData ?? { page: 1, limit: 20 };

  const {
    data: blogDetailData,
    isLoading: detailLoading,
    refetch: refetchBlogDetails,
  } = useBlogDetails(selectedBlogId || "", {
    enabled: !!selectedBlogId,
  });

  const detailBlog = blogDetailData?.data;

  const { mutate: saveBlog, isPending: isSaving } = useSaveBlog({ qData });
  const { mutate: deleteBlog } = useDeleteBlog({ qData });
  const { mutate: toggleStatus } = useBlogStatus({ qData });

  const watchHeading = Form.useWatch(["banner", "heading"], form);

  

  const triggerRefetch = () => {
    externalRefetch?.();
    refetchBlogs();
    refetchAuthors();
  };

  useEffect(() => {
    if (!selectedBlogId && watchHeading) {
      form.setFieldValue("slug", removeSpecialChars(watchHeading));
    }
  }, [watchHeading, selectedBlogId, form]);

  useEffect(() => {
    if (detailBlog && selectedBlogId) {
      form.resetFields();
      form.setFieldsValue({
        authorId: detailBlog.authorId,
        slug: detailBlog.slug,
        order: detailBlog.order,
        short_description: detailBlog.short_description || "",
        metaTitle: detailBlog.metaTitle,
        metaDescription: detailBlog.metaDescription,
        banner: {
          heading: detailBlog.banner?.heading || "",
          image: detailBlog.banner?.image || "",
        },
        overview: {
          heading: detailBlog.overview?.heading || "",
          subtext: detailBlog.overview?.subtext?.length
            ? detailBlog.overview.subtext
            : [""],
          image: detailBlog.overview?.image || "",
        },
        pestControl: detailBlog.pestControl?.length
          ? detailBlog.pestControl.map((item) => ({
              title: item.title || "",
              subtext: item.subtext || "",
              subtitle: item.subtitle || "",
              points: item.points?.length ? item.points : [""],
              image: item.image || "",
            }))
          : [
              {
                title: "",
                subtitle: "",
                subtext: "",
                points: [""],
              },
            ],
        status: detailBlog.status ?? true,
      });
    }
  }, [detailBlog, form, selectedBlogId]);

  const handleAdd = () => {
    refetchAuthors();
    setSelectedBlogId(null);
    form.resetFields();
    form.setFieldsValue({
      status: true,
      overview: {
        subtext: [""],
      },
      pestControl: [
        {
          title: "",
          subtitle: "",
          subtext: "",
          points: [""],
        },
      ],
    });
    setIsEditing(true);
  };

  const handleEdit = (blog: IBlogListPayload) => {
    refetchAuthors();
    setSelectedBlogId(blog._id || null);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setSelectedBlogId(null);
    form.resetFields();
    setIsEditing(false);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      const payload: IBlogPayload = {
        ...(selectedBlogId ? { _id: selectedBlogId } : {}),
        authorId: values.authorId || "",
        slug: values.slug || removeSpecialChars(values.banner?.heading || ""),
        order: values.order ?? 0,
        short_description: values.short_description || "",
        metaTitle: values.metaTitle || "",
        metaDescription: values.metaDescription || "",
        banner: {
          heading: values.banner?.heading || "",
          image:
            getSingleImageUrl(values.banner?.image) ||
            detailBlog?.banner?.image ||
            "",
        },
        overview: {
          heading: values.overview?.heading || "",
          subtext: values.overview?.subtext || [],
          image:
            getSingleImageUrl(values.overview?.image) ||
            detailBlog?.overview?.image ||
            "",
        },
        pestControl:
          values.pestControl?.map((item: any, index: number) => ({
            title: item.title || "",
            subtext: item.subtext || "",
            subtitle: item.subtitle || "",
            points: item.points || [],
            image:
              getSingleImageUrl(item.image) ||
              detailBlog?.pestControl?.[index]?.image ||
              "",
          })) || [],
        status: values.status ?? true,
      };

      saveBlog(payload, {
        onSuccess: () => {
          messageApi.success(selectedBlogId ? "Blog updated" : "Blog created");
          handleCancel();
          triggerRefetch();
        },
        onError: () => messageApi.error("Failed to save blog"),
      });
    } catch (err) {
      console.error("Validation failed:", err);
    }
  };

  const handleStatusToggle = (id: string) => {
    toggleStatus(
      { ids: id },
      {
        onSuccess: () => {
          messageApi.success("Status Changed Successfully");
          triggerRefetch();
        },
        onError: () => messageApi.error("Status Chnage failed"),
      },
    );
  };

  const handleDelete = (id: string) => {
    deleteBlog(
      { ids: id },
      {
        onSuccess: () => {
          messageApi.success("Blog deleted");
          triggerRefetch();
        },
        onError: () => messageApi.error("Delete failed"),
      },
    );
  };

  return (
    <>
      {contextHolder}

      {!isEditing ? (
        <>
          <div className="flex justify-end mb-4">
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              Add Blog
            </Button>
          </div>

          <div className="rounded-[20px] overflow-hidden hidden sm:block">
            <table className="min-w-full">
              <thead>
                <tr className="bg-primary text-white">
                  <th className="py-3 px-4 text-left">Title</th>
                  <th className="py-3 px-4 text-left">Author</th>
                  <th className="py-3 px-4 text-left">Status</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {blogList?.map((blog) => {
                  return (
                    <tr key={blog._id} className="bg-[#F9F9F9] border-b-2">
                      <td className="py-3 px-4 font-medium max-w-xs truncate">
                        {blog.heading}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        <div className="flex items-center gap-4">
                          <span>{blog?.authorDetails?.name || "Unknown"}</span>
                          <img
                            src={blog?.authorDetails?.avatar}
                            className="w-6 h-6 object-cover rounded-full"
                          />
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <Switch
                          checked={blog.status}
                          checkedChildren="Published"
                          unCheckedChildren="Draft"
                          onChange={() => handleStatusToggle(blog._id ?? "")}
                        />
                      </td>
                      <td className="py-3 px-4 text-right">
                        <EditOutlined
                          onClick={() => handleEdit(blog)}
                          className="cursor-pointer text-primary text-xl mr-4"
                        />
                        <Popconfirm
                          title="Delete blog?"
                          onConfirm={() => handleDelete(blog._id ?? "")}
                          okText="Yes"
                          cancelText="No"
                        >
                          <DeleteOutlined className="cursor-pointer text-red-600 text-xl" />
                        </Popconfirm>
                      </td>
                    </tr>
                  );
                })}
                {blogList.length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-center py-10 text-gray-500">
                      No blogs found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-2 hidden sm:block">
            <MyPagination
              qData={qData}
              onChange={() => refetchBlogs()}
              total={qData.total}
              DEFAULT_NUQS_CONFIG={DEFAULT_NUQS_CONFIG}
            />
          </div>

          <div className="sm:hidden space-y-4">
            {blogList.map((blog) => {
              return (
                <div key={blog._id} className="bg-[#F9F9F9] p-4 rounded-lg">
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <h4 className="font-semibold text-lg line-clamp-2">
                        {blog.heading}
                      </h4>
                      <p className="text-sm text-gray-600">
                        by{" "}
                        <strong>
                          {blog?.authorDetails?.name || "Unknown"}
                        </strong>
                      </p>
                      <div className="mt-2">
                        <Switch
                          checked={blog.status}
                          checkedChildren="Published"
                          unCheckedChildren="Draft"
                          onChange={() => handleStatusToggle(blog._id ?? "")}
                        />
                      </div>
                    </div>
                    <div className="flex flex-col gap-3">
                      <EditOutlined
                        onClick={() => handleEdit(blog)}
                        className="text-primary text-xl"
                      />
                      <Popconfirm
                        title="Delete?"
                        onConfirm={() => handleDelete(blog._id ?? "")}
                      >
                        <DeleteOutlined className="text-red-600 text-xl" />
                      </Popconfirm>
                    </div>
                  </div>
                </div>
              );
            })}
            {blogList.length === 0 && (
              <div className="text-center py-10 text-gray-500">
                No blogs found
              </div>
            )}
          </div>

          <div className="mt-2 sm:hidden">
            <MyPagination
              qData={qData}
              onChange={() => refetchBlogs()}
              total={qData.total}
              DEFAULT_NUQS_CONFIG={DEFAULT_NUQS_CONFIG}
            />
          </div>
        </>
      ) : (
        <div className="bg-[#F9F9F9] p-6 rounded-xl">
          {detailLoading && selectedBlogId ? (
            <div className="text-center py-10 flex gap-6 items-center">
              <LoaderCircleIcon className="animate-spin" />{" "}
              <p>Loading blog details...</p>
            </div>
          ) : (
            <Form form={form} layout="vertical">
              <h3 className="text-lg font-semibold mt-6">Banner Section</h3>

              <Form.Item label="Heading" name={["banner", "heading"]}>
                <Input placeholder="Banner heading" />
              </Form.Item>

              <Form.Item label="Image" name={["banner", "image"]}>
                <UploadFoodImage maxUploadCount={1} />
              </Form.Item>

              <Form.Item
                label="Slug"
                name="slug"
                rules={[{ required: true, message: "Slug is required" }]}
              >
                <Input placeholder="auto-generated" />
              </Form.Item>
              <Form.Item
                label="Meta Title"
                name="metaTitle"
                rules={[{ required: true, message: "Meta Title is required" }]}
              >
                <Input placeholder="enter meta title..." />
              </Form.Item>
              <Form.Item
                label="Meta Description"
                name="metaDescription"
                rules={[{ required: true, message: "Meta Description is required" }]}
              >
                <TextArea rows={2} placeholder="enter meta description..." />
              </Form.Item>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item label="Order" name="order">
                    <InputNumber min={0} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Author"
                    name="authorId"
                    rules={[
                      { required: true, message: "Please select an author" },
                    ]}
                  >
                    <Select
                      placeholder="Select author"
                      loading={authors.length === 0}
                      showSearch
                      optionFilterProp="label"
                      options={authors
                        .filter((a) => a.status !== false)
                        .map((author) => ({
                          value: author._id,
                          label: (
                            <div className="flex items-center gap-4">
                              <span>{author.name}</span>
                              <img
                                src={author.avatar}
                                alt={author.name}
                                className="w-6 h-6 rounded-full object-cover"
                              />
                            </div>
                          ),
                        }))}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item label="Short Description" name="short_description">
                <TextArea placeholder="Short description..." rows={3} />
              </Form.Item>

              <h3 className="text-lg font-semibold mt-6">Overview Section</h3>

              <Form.Item label="Heading" name={["overview", "heading"]}>
                <Input placeholder="Overview heading" />
              </Form.Item>

              <h3 className="text-sm mb-2">Subtext</h3>
              <Form.List name={["overview", "subtext"]}>
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <Row key={key} gutter={8} align="middle" className="mb-3">
                        <Col flex="auto">
                          <Form.Item
                            {...restField}
                            name={name}
                            rules={[
                              {
                                required: true,
                                message: "Please enter subtext",
                              },
                            ]}
                          >
                            <TextArea
                              placeholder="Overview subtext..."
                              autoSize={{ minRows: 2, maxRows: 4 }}
                            />
                          </Form.Item>
                        </Col>
                        <Col>
                          <Button
                            danger
                            icon={<MinusOutlined />}
                            onClick={() => remove(name)}
                            disabled={fields.length === 1}
                            shape="circle"
                          />
                        </Col>
                      </Row>
                    ))}
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      block
                      icon={<PlusOutlined />}
                    >
                      Add Subtext
                    </Button>
                  </>
                )}
              </Form.List>

              <Form.Item label="Overview Image" name={["overview", "image"]}>
                <UploadFoodImage maxUploadCount={1} />
              </Form.Item>

              <h3 className="text-lg font-semibold mt-6">
                Pest Control Sections
              </h3>

              <Form.List name="pestControl">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <div
                        key={key}
                        className="border border-gray-300 p-5 rounded-xl mb-6 bg-white shadow-sm"
                      >
                        <div className="flex justify-between items-center mb-4">
                          <h4 className="text-base font-medium">
                            Pest Control Section{" "}
                            {fields.length > 1 ? `#${Number(name) + 1}` : ""}
                          </h4>
                          <Button
                            danger
                            icon={<MinusOutlined />}
                            onClick={() => remove(name)}
                            disabled={fields.length === 1}
                            shape="circle"
                          />
                        </div>

                        <Form.Item
                          label="Title"
                          name={[name, "title"]}
                          rules={[
                            { required: true, message: "Title is required" },
                          ]}
                        >
                          <Input placeholder="e.g. Termite Control" />
                        </Form.Item>

                        <Form.Item label="Subtext" name={[name, "subtext"]}>
                          <TextArea
                            rows={3}
                            placeholder="Short description..."
                          />
                        </Form.Item>

                        <Form.Item label="Subtitle" name={[name, "subtitle"]}>
                          <Input placeholder="enter subtitle" />
                        </Form.Item>

                        <Form.List name={[name, "points"]}>
                          {(
                            pointFields,
                            { add: addPoint, remove: removePoint },
                          ) => (
                            <>
                              <p className="font-medium mb-2">Key Points</p>
                              {pointFields.map(
                                ({ key: pKey, name: pName, ...pRest }) => (
                                  <Row
                                    key={pKey}
                                    gutter={8}
                                    align="middle"
                                    className="mb-2"
                                  >
                                    <Col flex="auto">
                                      <Form.Item
                                        {...pRest}
                                        name={pName}
                                        rules={[
                                          {
                                            required: true,
                                            message: "Point is required",
                                          },
                                        ]}
                                      >
                                        <Input placeholder="• Bullet point" />
                                      </Form.Item>
                                    </Col>
                                    <Col>
                                      <Button
                                        danger
                                        icon={<MinusOutlined />}
                                        onClick={() => removePoint(pName)}
                                        disabled={pointFields.length === 1}
                                        shape="circle"
                                      />
                                    </Col>
                                  </Row>
                                ),
                              )}
                              <Button
                                type="dashed"
                                onClick={() => addPoint()}
                                block
                                icon={<PlusOutlined />}
                              >
                                Add Point
                              </Button>
                            </>
                          )}
                        </Form.List>

                        <Form.Item
                          label="Section Image"
                          name={[name, "image"]}
                          className="mt-4"
                        >
                          <UploadFoodImage maxUploadCount={1} />
                        </Form.Item>
                      </div>
                    ))}

                    <Button
                      type="dashed"
                      onClick={() => add()}
                      block
                      icon={<PlusOutlined />}
                      size="middle"
                    >
                      Add New Pest Control Section
                    </Button>
                  </>
                )}
              </Form.List>

              <Form.Item
                label="Status"
                name="status"
                valuePropName="checked"
                className="mt-6"
              >
                <Switch checkedChildren="Published" unCheckedChildren="Draft" />
              </Form.Item>
            </Form>
          )}

          <div className="flex justify-end gap-3 mt-8">
            <ButtonWrapper onClick={handleCancel}>Cancel</ButtonWrapper>
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={isSaving || detailLoading}
              disabled={detailLoading}
            >
              {selectedBlogId ? "Update" : "Create"} Blog
            </ButtonWrapper>
          </div>
        </div>
      )}
    </>
  );
};

export default BlogListingSection;
